import { EMPTY_MARK } from '@/components/ui';
import { FLEET_STATUS_LABELS, FleetStatus, RateMode, Vehicle, VehicleAvailability } from '../../../types/Vehicle';
import { formatDateTime } from '../../../utils/wrapDate';
import { VISIBILITY_FAIL_LABELS } from './equipmentPermissions';

/**
 * Display formatters for the Equipment module. Every value shown to a user goes through one of
 * these (LANG-06): no raw enum, code or field name reaches the screen. Empty values are the grey
 * en dash (FMT-10), never an em dash or hyphen.
 */

/** "Grader · John Deere 772G · 2021" style summary, matching the prototype's combined column. */
export function typeMakeModelYear(vehicle: Vehicle): string {
  const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const parts = [vehicle.vehicleClassification, makeModel || undefined, vehicle.year ? String(vehicle.year) : undefined];
  return parts.filter(Boolean).join(' · ') || EMPTY_MARK;
}

export type RoadType = 'On-Road' | 'Off-Road';

/** The one label for the On-Road / Off-Road field (EQ-28). */
export const ROAD_TYPE_LABEL: Record<RoadType, string> = { 'On-Road': 'On-Road', 'Off-Road': 'Off-Road' };

/** 'on-road', 'ON_ROAD', 'Onroad' -> 'On-Road'. Anything else -> null. */
export function normalizeRoadType(value?: string | null): RoadType | null {
  if (!value) return null;
  const compact = value.toLowerCase().replace(/[\s_-]+/g, '');
  if (compact === 'onroad') return 'On-Road';
  if (compact === 'offroad') return 'Off-Road';
  return null;
}

/** vehicleType is WRAP's On-Road / Off-Road field; roadClass is the fuel enum kept in parallel. */
export function roadTypeOf(vehicle: Pick<Vehicle, 'vehicleType' | 'roadClass'>): RoadType | null {
  return normalizeRoadType(vehicle.vehicleType) ?? normalizeRoadType(vehicle.roadClass);
}

export function unitTypeLabel(vehicle: Pick<Vehicle, 'vehicleType' | 'roadClass'>): string {
  return roadTypeOf(vehicle) ?? EMPTY_MARK;
}

/** "Service Area 25" (FMT-21); a bare code like "25" or "SA25" becomes "Service Area 25". */
export function serviceAreaLabel(area: { name?: string | null; code?: string | null } | null | undefined): string {
  if (!area) return EMPTY_MARK;
  const name = area.name?.trim();
  if (name && /^service area\s+\d+/i.test(name)) return name.replace(/^service area/i, 'Service Area');
  const code = (area.code ?? '').replace(/^SA/i, '').trim();
  if (code && /^\d+$/.test(code)) return `Service Area ${code}`;
  return name || EMPTY_MARK;
}

/** Friendly label for an equipment status value, falling back to the raw value for unknown data. */
export function fleetStatusLabel(status: string | undefined | null): string {
  if (!status) return EMPTY_MARK;
  return FLEET_STATUS_LABELS[status as FleetStatus] || status;
}

/** Currency as $1,154.80 (FMT-16). */
export function formatMoney(value: number): string {
  return value.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' });
}

/**
 * "RC-01 · $131.00" for a rate-class unit, "$142.00 (Custom)" for a custom rate, or just the
 * amount when rateMode has not been set yet (most existing records).
 */
export function rateDisplay(vehicle: Vehicle): string {
  if (vehicle.rate == null) return EMPTY_MARK;
  const amount = formatMoney(vehicle.rate);
  if (vehicle.rateMode === RateMode.Custom) return `${amount} (Custom)`;
  if (vehicle.rateMode === RateMode.RateClass && vehicle.rateClassCode) return `${vehicle.rateClassCode} · ${amount}`;
  return amount;
}

/** Badge colour for an equipment status value. */
export function fleetStatusBadgeVariant(status: string | undefined | null): string {
  switch (status) {
    case FleetStatus.Active:
      return 'success';
    case FleetStatus.Inactive:
      return 'secondary';
    case FleetStatus.StoredServiceable:
      return 'info';
    case FleetStatus.Mothballed:
      return 'dark';
    case FleetStatus.PartsUnit:
      return 'warning';
    case FleetStatus.Retired:
      return 'danger';
    default:
      return 'secondary';
  }
}

/** Badge colour for availability; no availability is neutral, the same everywhere (EQ-17). */
export function availabilityBadgeVariant(currentStatus: string | undefined | null): string {
  if (!currentStatus) return 'secondary';
  const lower = currentStatus.toLowerCase();
  if (lower === 'in service' || currentStatus === VehicleAvailability.InService) return 'success';
  if (lower === 'out of service' || currentStatus === VehicleAvailability.OutOfService) return 'danger';
  return 'secondary';
}

export function unitAvailabilityLabel(vehicle: Vehicle): string {
  const fromServer = vehicle.availabilityLabel;
  if (fromServer && fromServer !== '—' && fromServer !== EMPTY_MARK) return titleAvailability(fromServer);
  if (vehicle.availability === VehicleAvailability.InService) return 'In Service';
  if (vehicle.availability === VehicleAvailability.OutOfService) return 'Out of Service';
  return vehicle.currentStatus ? titleAvailability(vehicle.currentStatus) : EMPTY_MARK;
}

function titleAvailability(value: string): string {
  const lower = value.toLowerCase();
  if (lower === 'in service') return 'In Service';
  if (lower === 'out of service') return 'Out of Service';
  return value;
}

/** Whether the unit's status lets it be put in service or transferred (EQ-10). */
export function isActiveUnit(vehicle: Pick<Vehicle, 'status'>): boolean {
  return vehicle.status === FleetStatus.Active || vehicle.status === 'Active';
}

export function visibilityFailReasons(vehicle: Vehicle): string[] {
  return (vehicle.driverVisibility?.failed || []).map((key) => VISIBILITY_FAIL_LABELS[key] || key);
}

export function isPmOverdue(vehicle: Vehicle): boolean {
  return vehicle.currentOdometer != null && vehicle.nextPMDue != null && vehicle.currentOdometer >= vehicle.nextPMDue;
}

export function isGreasingOverdue(vehicle: Vehicle): boolean {
  return vehicle.currentOdometer != null && vehicle.nextGreasing != null && vehicle.currentOdometer >= vehicle.nextGreasing;
}

/** Last odometer reported date and time, "2026-10-23 8:00 AM" Pacific. */
export function formatLastReportedDateTime(value?: string | Date | null): string {
  return formatDateTime(value ?? null) ?? EMPTY_MARK;
}

/** km or hours, from the Vehicle Profile's odometer alerts (EQ-19). Lowercase (FMT-25). */
export function meterUnitOf(vehicle: Pick<Vehicle, 'vehicleProfile'>): 'km' | 'hours' {
  return /hour|hr/i.test(vehicle.vehicleProfile?.odometerAlerts ?? '') ? 'hours' : 'km';
}

/** "375,118 km" (EQ-19): thousands separators and the unit on every reading. */
export function formatMeterReading(value?: number | null, unit?: 'km' | 'hours'): string {
  if (value == null) return EMPTY_MARK;
  const number = value.toLocaleString('en-CA');
  return unit ? `${number} ${unit}` : number;
}

/** Next PM line: "Minor at 10,250 km (1,201 km remaining)" or "… – overdue by 118 km". */
export function formatNextPmLine(vehicle: Vehicle): { text: string; overdue: boolean } {
  if (vehicle.nextPMDue == null) return { text: EMPTY_MARK, overdue: false };
  const unit = meterUnitOf(vehicle);
  const overdue = isPmOverdue(vehicle);
  const due = formatMeterReading(vehicle.nextPMDue, unit);
  const head = vehicle.nextPMType ? `${vehicle.nextPMType} at ${due}` : due;
  if (vehicle.currentOdometer == null) return { text: head, overdue };
  const remaining = vehicle.nextPMDue - vehicle.currentOdometer;
  const suffix = remaining <= 0
    ? ` – overdue by ${formatMeterReading(Math.abs(remaining), unit)}`
    : ` (${formatMeterReading(remaining, unit)} remaining)`;
  return { text: `${head}${suffix}`, overdue };
}

export type CvipDueState = 'overdue' | 'soon' | null;

export function cvipDueState(nextCVIP?: string | null, now: Date = new Date()): CvipDueState {
  if (!nextCVIP) return null;
  const due = new Date(nextCVIP);
  if (Number.isNaN(due.getTime())) return null;
  const startToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const startDue = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  if (startDue < startToday) return 'overdue';
  const days = (startDue - startToday) / 86400000;
  if (days <= 30) return 'soon';
  return null;
}

/** Old descriptions join category and text with an em dash, new ones with an en dash. */
const DASH_SEPARATOR = / [—–] /;

/** Inspection item vs reporter comment from a stored defect description. */
export function splitDefectDescription(description: string): { item: string; comments: string } {
  const text = String(description || '');
  const newline = text.indexOf('\n');
  if (newline !== -1) {
    return { item: text.slice(0, newline).trim(), comments: text.slice(newline + 1).trim() };
  }
  const legacy = text.match(/^(.*? [—–] .+?): ([\s\S]+)$/);
  if (legacy) return { item: legacy[1].trim(), comments: legacy[2].trim() };
  return { item: text.trim(), comments: '' };
}

/**
 * Split an inspection item ("Under Hood — Check radiator — Fail") into its category and the
 * defect itself. A trailing "Fail" marker is dropped. Items without a category come back whole.
 * The stored text is never rewritten; parts are joined back with an en dash for display.
 */
export function splitDefectCategory(item: string): { category: string; defect: string } {
  const parts = String(item || '').split(DASH_SEPARATOR).map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return { category: '', defect: String(item || '').trim() };
  const [category, ...rest] = parts;
  if (rest.length > 1 && /^fail$/i.test(rest[rest.length - 1])) rest.pop();
  return { category, defect: rest.join(' – ') };
}

/**
 * A defect's category and text for display (EQ-13): the stored category when there is one,
 * otherwise the category folded into an older description.
 */
export function defectParts(defect: { category?: string | null; description: string }): {
  category: string;
  defect: string;
  comments: string;
} {
  const { item, comments } = splitDefectDescription(defect.description);
  if (defect.category) {
    const split = splitDefectCategory(item);
    const sameCategory = split.category && split.category.toLowerCase() === defect.category.toLowerCase();
    return { category: defect.category, defect: sameCategory ? split.defect : item, comments };
  }
  const split = splitDefectCategory(item);
  return { category: split.category, defect: split.defect, comments };
}

export function defectSourceLabel(source: string): string {
  if (source === 'TripInspection') return 'Trip Inspection';
  if (source === 'PmInspection') return 'PM Inspection';
  if (source === 'TimeCard') return 'Time Card';
  if (source === 'Wrap') return 'WRAP';
  return source;
}

/** Friendly defect status (LANG-06, EQ-07). */
export const DEFECT_STATUS_LABEL: Record<string, string> = {
  Open: 'Open',
  Closed: 'Closed'
};

export function defectStatusLabel(status: string): string {
  return DEFECT_STATUS_LABEL[status] ?? status;
}

export const DEFECT_OUTCOME_LABEL: Record<string, string> = {
  Repaired: 'Repaired',
  Duplicate: 'Duplicate',
  NoFaultFound: 'No Fault Found'
};

/** Odometer source labels (EQ-19, LANG-06). */
export function odometerSourceLabel(source?: string | null): string {
  if (!source) return EMPTY_MARK;
  const key = source.replace(/[\s_-]+/g, '').toLowerCase();
  if (key === 'pminspection' || key === 'pmserviceandinspection') return 'PM Inspection';
  if (key === 'tripinspection' || key === 'vehicletripinspection') return 'Trip Inspection';
  if (key === 'wrapoverride') return 'WRAP Override';
  if (key === 'wrap') return 'WRAP';
  if (key.startsWith('dailylog')) return 'Daily Log – Mechanic';
  return source;
}

export function personName(person?: { name?: string | null; lastName?: string | null } | null): string {
  if (!person) return '';
  return [person.name, person.lastName].filter(Boolean).join(' ').trim();
}

/** "1 open defect", "12 open defects" (LANG-08). */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString('en-CA')} ${count === 1 ? singular : plural}`;
}

/**
 * Fallback categories for Add a Defect while the server list loads; the server's list
 * (GET /api/vehicles/defect-categories) is the one used.
 */
export const DEFECT_CATEGORIES = [
  'Driver Area Cleaning / Housekeeping',
  'Fluid Levels and Lubrication',
  'Body & Frame',
  'Valid insurance and license plate decal',
  'Lighting devices and reflectors',
  'Windshield and Wipers',
  'Hubs / Tires / Wheels / Rims',
  'Trailer brake connections (if using trailer)',
  'Attachments / Sweeper',
  'Coupling Devices',
  'Cargo security and load securement',
  'Braking System',
  'Brake Adjustment',
  'Parking brake',
  'Fuel System',
  'Power train (engine, clutch, transmission)',
  'Steering Mechanism',
  'Exhaust System',
  'Suspension',
  'Blades / Ground Engaging',
  'Electrical',
  'Dash Lights and Gauges',
  'Horn',
  'Interior / Cab Condition / Housekeeping',
  'Driver',
  'Emergency equipment',
  'Conveyor “unload” for 30+ seconds (Post-trip only)',
  'Other'
] as const;
